import { Component, OnInit } from '@angular/core';
import { PagedResultDto, LocalizationService, ConfigStateService } from '@abp/ng.core';
import { Column } from '../shared/components/metro-table/metro-table.component';
import { CouponDto, GetCouponsInput, CouponReportDto, CouponReportFilterDto } from '../proxy/coupons/models';
import { CouponService } from '@proxy/coupons';
import { ConfirmationModalService } from '../shared/services/confirmation-modal.service';
import { Router } from '@angular/router';
import { SommelierService } from '@proxy/sommeliers';
import { CouponDiscountType } from '../proxy/enums/coupon-discount-type.enum';
import { SelectOption } from '../shared/components/metro-form-select/metro-form-select.component';
import { MetroToastService } from '../shared/services/metro-toast.service';
import { HostListener } from '@angular/core';
import { finalize, take } from 'rxjs';
import { APP_ICONS } from '../shared/icons/app-icons';

@Component({
  selector: 'app-coupons',
  templateUrl: './coupons.component.html',
  standalone: false,
})
export class CouponsComponent implements OnInit {
  activeTab: 'list' | 'report' = 'list';

  columns: Column[] = [
    { name: 'code', label: '::Code', width: '15%' },
    { name: 'discountValueDisplay', label: 'Descuento', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'startDateDisplay', label: 'Fecha de inicio', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'endDateDisplay', label: 'Fecha de fin', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'uses', label: '::Uses', width: '10%', textColor: 'text-metro-blue-secondary' },
    { name: 'sommelierName', label: '::Sommelier', width: '15%', textColor: 'text-metro-blue-secondary' },
    { name: 'statusDisplay', label: '::Status', width: '15%' },
  ];

  // Report Columns
  reportColumns: Column[] = [
    { name: 'couponCode', label: '::Code', width: '20%' },
    { name: 'usedCount', label: '::UsedCount', width: '15%' },
    { name: 'totalDiscountDisplay', label: '::TotalDiscount', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'sommelierName', label: '::Sommelier', width: '20%', textColor: 'text-metro-blue-secondary' },
    { name: 'lastUsedDisplay', label: '::LastUsed', width: '25%', textColor: 'text-metro-blue-secondary' },
  ];

  actions: any[] = [
    { name: 'view', label: '::View', handler: (row: CouponDto) => this.openViewModal(row) },
    { name: 'edit', label: '::Edit', handler: (row: CouponDto) => this.openEditModal(row) },
    { 
      name: 'toggleActive', 
      label: (row: CouponDto) => row.isActive ? '::Deactivate' : '::Activate', 
      handler: (row: CouponDto) => this.toggleActive(row) 
    }
  ];

  data: PagedResultDto<CouponDto> = { items: [], totalCount: 0 };
  searchCoupons = {} as GetCouponsInput;
  
  reportData: CouponReportDto[] = [];
  reportFilter = {} as CouponReportFilterDto;
  
  isLoading = true;
  isExporting = false;
  currentPage = 1;
  maxResultCount = 3;
  isCheckingStatus = false;
  
  excelIcon = APP_ICONS.excel;

  // Report mobile pagination
  reportCurrentPage = 1;
  reportPageSize = 3;

  sommelierOptions: SelectOption[] = [];
  statusOptions: SelectOption[] = [];
  typeOptions: SelectOption[] = [];
  isAdmin = false;

  constructor(
    private couponService: CouponService,
    private sommelierService: SommelierService,
    private confirmation: ConfirmationModalService,
    private router: Router,
    private localizationService: LocalizationService,
    private toast: MetroToastService,
    private config: ConfigStateService
  ) {}

  ngOnInit(): void {
    this.updatePageSize();
    this.initOptions();
    this.loadSommeliers();
    this.loadCoupons();
    this.checkAdminRole();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    const oldSize = this.maxResultCount;
    this.updatePageSize();
    if (oldSize !== this.maxResultCount) {
      this.currentPage = 1;
      this.reportCurrentPage = 1;
      if (this.activeTab === 'list') this.loadCoupons();
    }
  }

  private updatePageSize() {
    const isMobile = window.innerWidth < 768;
    this.maxResultCount = isMobile ? 3 : 10;
    this.reportPageSize = isMobile ? 3 : 10;
  }

  checkAdminRole() {
    const user = this.config.getOne('currentUser');
    const roles = user?.roles?.map(r => r.toLowerCase()) ?? [];
    this.isAdmin = roles.includes('admin');
  }

  initOptions() {
    this.statusOptions = [
      { label: 'Todos', value: null },
      { label: 'Activo', value: 1 },
      { label: 'Expirado', value: 2 },
      { label: 'Inactivo', value: 4 },
    ];

    this.typeOptions = [
      { label: this.localizationService.instant('::All'), value: null },
      { label: this.localizationService.instant('::Percentage'), value: CouponDiscountType.Percentage },
      { label: this.localizationService.instant('::Fixed'), value: CouponDiscountType.Fixed },
    ];
  }

  loadCoupons() {
    this.isLoading = true;
    
    // Validamos que la página sea un número válido
    if (isNaN(this.currentPage) || this.currentPage < 1) {
      this.currentPage = 1;
    }

    const skipCount = (this.currentPage - 1) * this.maxResultCount;

    const params: GetCouponsInput = {
      filter: this.searchCoupons.filter,
      status: this.searchCoupons.status ?? undefined,
      sommelierId: this.searchCoupons.sommelierId ?? undefined,
      discountType: this.searchCoupons.discountType ?? undefined,
      skipCount,
      maxResultCount: this.maxResultCount,
    };

    this.couponService.getList(params).subscribe((result) => {
      this.data = {
        ...result,
        items: (result.items || []).map(item => {
          const isPercentage = item.discountType === 0;
          return {
            ...item,
            discountValueDisplay: isPercentage ? `${item.discountValue}%` : `$${item.discountValue.toLocaleString('es-AR')}`,
            startDateDisplay: item.startDate ? new Date(item.startDate).toLocaleDateString('es-AR', { day:'numeric', month:'numeric', year:'numeric' }) : '---',
            endDateDisplay: item.endDate ? new Date(item.endDate).toLocaleDateString('es-AR', { day:'numeric', month:'numeric', year:'numeric' }) : '---',
            uses: `${item.usedCount || 0} / ${item.maxUses || '∞'}`,
            sommelierName: item.sommelierName || 'Todos',
            statusDisplay: this.getStatusLabel(item.status)
          };
        })
      };
      this.isLoading = false;
    }, error => {
      console.error('Error cargando cupones:', error);
      this.isLoading = false;
    });
  }

  getStatusLabel(status: any): string {
    const statusMap = {
      0: 'Inactive',
      1: 'Active',
      2: 'Expired',
      3: 'Inactive',
      4: 'Inactive'
    };
    const key = statusMap[status] || 'Unknown';
    return this.localizationService.instant(`::${key}`);
  }

  getStatusBadgeClass(status: any): string {
    const classMap = {
      0: 'bg-gray-100 text-gray-500',
      1: 'bg-green-100 text-green-700',
      2: 'bg-orange-100 text-orange-600',
      3: 'bg-gray-100 text-gray-500',
      4: 'bg-gray-100 text-gray-500'
    };
    return classMap[status] || 'bg-gray-100 text-gray-500';
  }

  getStatusAbbr(status: any): string {
    const label = this.getStatusLabel(status);
    return label.substring(0, 3) + '.';
  }

  getReportPageItems(): any[] {
    const start = (this.reportCurrentPage - 1) * this.reportPageSize;
    return this.reportData.slice(start, start + this.reportPageSize);
  }

  getReportTotalPages(): number {
    return Math.max(1, Math.ceil(this.reportData.length / this.reportPageSize));
  }

  getReportPageNumbers(): number[] {
    const total = this.getReportTotalPages();
    const current = this.reportCurrentPage;
    const pages: number[] = [];
    const start = Math.max(1, current - 2);
    const end = Math.min(total, start + 4);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  onReportPageChange(page: number): void {
    if (page < 1 || page > this.getReportTotalPages()) return;
    this.reportCurrentPage = page;
  }

  getTotalPages(): number {
    return Math.ceil(this.data.totalCount / this.maxResultCount);
  }

  getPageNumbers(): number[] {
    const total = this.getTotalPages();
    const pages: number[] = [];
    for (let i = 1; i <= total; i++) pages.push(i);
    return pages;
  }

  getUsagePercentage(item: any): number {
    if (!item.maxUses) return 0;
    const percentage = ((item.usedCount || 0) / item.maxUses) * 100;
    return Math.min(percentage, 100);
  }

  loadSommeliers() {
    this.sommelierService.getList({ maxResultCount: 1000, status: 'Approved' } as any).subscribe(result => {
      this.sommelierOptions = [
        { label: this.localizationService.instant('::All'), value: null },
        ...result.items.map(s => ({ label: s.fullName, value: s.userId }))
      ];
    });
  }

  loadReport() {
    this.isLoading = true;
    const params: CouponReportFilterDto = {
      fromDate: this.reportFilter.fromDate ?? undefined,
      toDate: this.reportFilter.toDate ?? undefined,
      sommelierId: this.reportFilter.sommelierId ?? undefined,
      filter: this.searchCoupons.filter ?? undefined
    };

    this.couponService.getReport(params).subscribe(result => {
      this.reportData = result.map(item => ({
        ...item,
        sommelierName: item.sommelierName || 'Todos',
        totalDiscountDisplay: `$${item.totalDiscount}`,
        firstUsedDisplay: item.firstUsed ? new Date(item.firstUsed + (!item.firstUsed.endsWith('Z') ? 'Z' : '')).toLocaleDateString('es-AR') : '-',
        lastUsedDisplay: item.lastUsed ? new Date(item.lastUsed + (!item.lastUsed.endsWith('Z') ? 'Z' : '')).toLocaleString() : '-'
      }));
      this.isLoading = false;
    }, error => {
      console.error('Error cargando reporte:', error);
      this.isLoading = false;
    });
  }

  setTab(tab: 'list' | 'report') {
    this.activeTab = tab;
    if (tab === 'report') {
      this.loadReport();
    } else {
      this.loadCoupons();
    }
  }

  onSearch(searchTerm: string) {
    this.searchCoupons.filter = searchTerm ? searchTerm.toUpperCase() : searchTerm;
    this.currentPage = 1;
    if (this.activeTab === 'list') {
      this.loadCoupons();
    } else {
      this.loadReport();
    }
  }

  onClearSearch() {
    this.searchCoupons.filter = undefined;
    this.currentPage = 1;
    if (this.activeTab === 'list') {
      this.loadCoupons();
    } else {
      this.loadReport();
    }
  }

  onFilterChange() {
    this.currentPage = 1;
    if (this.activeTab === 'list') {
      this.loadCoupons();
    } else {
      this.loadReport();
    }
  }

  openCreateModal() {
    this.router.navigate(['/coupons/create']);
  }

  openEditModal(coupon: CouponDto) {
    this.router.navigate(['/coupons/edit', coupon.id]);
  }

  openViewModal(coupon: CouponDto) {
    this.router.navigate(['/coupons/view', coupon.id]);
  }

  toggleActive(coupon: CouponDto) {
    const action = coupon.isActive ? '::Deactivate' : '::Activate';
    this.confirmation.confirm({
      title: action,
      message: '::AreYouSure',
      type: 'warning',
      showCancelButton: true,
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.couponService.update(coupon.id, { ...coupon, isActive: !coupon.isActive }).subscribe(() => this.loadCoupons());
    });
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.loadCoupons();
  }

  exportCsv() {
    this.isExporting = true;
    
    const params: CouponReportFilterDto = {
      fromDate: this.reportFilter.fromDate ?? undefined,
      toDate: this.reportFilter.toDate ?? undefined,
      sommelierId: this.reportFilter.sommelierId ?? undefined,
      filter: this.searchCoupons.filter ?? undefined
    };

    this.couponService.generateExcel(params, { responseType: 'blob' } as any)
      .pipe(
        take(1),
        finalize(() => this.isExporting = false)
      )
      .subscribe(blob => {
        const fileName = 'Cupones.xlsx';
        const downloadUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.click();

        window.URL.revokeObjectURL(downloadUrl);
      });
  }

  private saveCsv(headers: string, rows: string[], fileName: string) {
    const csvContent = "\uFEFF" + headers + "\n" + rows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  checkCouponsStatus() {
    this.isCheckingStatus = true;
    this.couponService.checkStatus().subscribe({
      next: (result) => {
        this.isCheckingStatus = false;
        if (result.updatedCount > 0) {
          this.toast.success(result.message, '::Success');
          this.loadCoupons();
        } else {
          this.toast.info(result.message, '::Information');
        }
      },
      error: (err) => {
        this.isCheckingStatus = false;
        this.toast.error('::ErrorCheckingStatus', '::Error');
        console.error('Error checking coupon status:', err);
      }
    });
  }
}
